import { api } from '@hunk/shared/api';
import type { OrderView } from '@hunk/shared/api/orders';
import { formatMoney } from '@hunk/shared/common';
import { Package } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router';

import { ProductImage } from '@/components/ProductCard';
import { errorText, useSession } from '@/hooks/useSession';
import { Spinner } from '@/pages/CheckoutPage';
import { OrderStatusBadge } from '@/pages/OrderPage';

/** "ההזמנות שלי" */
export function OrdersPage() {
  const { status, authHeaders } = useSession();
  const [orders, setOrders] = useState<OrderView[]>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    if (status !== 'authenticated') return;
    api.orders
      .myOrders({ headers: authHeaders() })
      .then((r) => setOrders(r.response.orders ?? []))
      .catch((err) => setError(errorText(err)));
  }, [status, authHeaders]);

  if (status === 'anonymous') return <Navigate to="/login?next=/orders" replace />;
  if (error) return <p className="py-24 text-center text-red-400">{error}</p>;
  if (!orders) return <Spinner />;

  return (
    <main className="mx-auto max-w-3xl px-3 py-6 md:px-4 md:py-10">
      <h1 className="mb-5 text-2xl font-extrabold">ההזמנות שלי</h1>
      {orders.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-neutral-400">
          <Package className="size-12" />
          <p>עוד אין הזמנות</p>
          <Link to="/" className="text-primary font-semibold hover:underline">
            לחנות
          </Link>
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {orders.map((o) => (
            <li key={o.id}>
              <Link to={`/order/${o.id}`} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-neutral-950 p-3 hover:border-white/30">
                <div className="flex -space-x-3 space-x-reverse">
                  {o.items.slice(0, 3).map((i) => (
                    <span key={`${i.productId}-${i.name}`} className="size-12 overflow-hidden rounded-lg border-2 border-black bg-white p-0.5">
                      <ProductImage src={i.image ?? undefined} alt="" />
                    </span>
                  ))}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold">{o.number}</span>
                    <OrderStatusBadge status={o.status} />
                  </div>
                  <span className="text-xs text-neutral-400">
                    {new Date(o.createdOn).toLocaleDateString('he-IL')} · {o.items.reduce((s, i) => s + i.qty, 0)} פריטים
                  </span>
                </div>
                <span className="font-extrabold">{formatMoney(o.total)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
